'use client';

import { type FC, useEffect, useRef } from 'react';

import styles from 'molecules/Video/video.module.scss';

import type { VideoConfigProps } from 'molecules/Video/config';

const WistiaVideoPlayer: FC<VideoConfigProps> = ({ embed }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!embed || !containerRef.current) {
      return;
    }

    import('dompurify').then(DOMPurify => {
      const purify = DOMPurify.default;

      purify.addHook('afterSanitizeAttributes', node => {
        if ('tagName' in node && node.tagName.toLowerCase() === 'script') {
          const src = node.getAttribute('src');
          if (src) {
            try {
              const parsedSrc = new URL(src);
              if (parsedSrc.protocol !== 'https:' || parsedSrc.hostname !== 'fast.wistia.com') {
                node.remove();
              }
            } catch {
              node.remove();
            }
          }
        }
      });

      const cleanEmbed = purify.sanitize(embed, {
        FORCE_BODY: true,
        ADD_TAGS: ['script'],
      });

      if (!containerRef.current) {
        return;
      }

      containerRef.current.innerHTML = cleanEmbed;

      const scripts = containerRef.current.querySelectorAll('script');
      scripts.forEach(script => {
        const newScript = document.createElement('script');
        if (script.src) {
          newScript.src = script.src;
        } else {
          newScript.textContent = script.textContent;
        }
        script.parentNode?.replaceChild(newScript, script);
      });
    });
  }, [embed]);

  if (!embed) {
    return null;
  }

  return <div className={styles.container} ref={containerRef} />;
};

export default WistiaVideoPlayer;
